import { formatVnd, parseVndInput } from './formatVnd';

/**
 * What RecordPaymentDrawer's amount field should display once its raw text
 * is committed (on blur). Blank input is preserved as blank rather than
 * reformatted to "0 ₫" — an untouched or deliberately cleared field must
 * still read as "nothing entered", not silently become a 0đ payment.
 *
 * A non-blank field is always reformatted, including when it parses to zero:
 * a fully-deducted invoice's default balance is exactly 0đ (see
 * RecordPaymentDrawer's defaultAmount), and that has to survive being
 * displayed, not get wiped back to blank the way it used to.
 */
export function commitAmountInput(raw: string): { amount: number; display: string } {
  if (raw.trim() === '') return { amount: 0, display: '' };
  const amount = parseVndInput(raw);
  return { amount, display: formatVnd(amount) };
}

/**
 * A payment amount is submittable iff the field actually holds a value — a
 * blank field must stay invalid even though its underlying `amount` state
 * defaults to 0 — and that value is zero or more. Negative is structurally
 * unreachable here: parseVndInput strips every non-digit character,
 * including a leading '-', so `amount` can never be negative in the first
 * place; the `>= 0` is a documented invariant, not a live guard.
 */
export function isSubmittableAmount(amount: number, amountInput: string): boolean {
  return amount >= 0 && amountInput.trim() !== '';
}
